import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, ArrowUp, ArrowDown, FileDown, Loader2, Edit2, Check, Copy, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import API from './apiConfig';
import { useModal } from './components/CustomModal';

/**
 * LV-Editor: Vollbild-Modal zum Bearbeiten aller Titel und Positionen
 * vor dem PDF-Export. Lädt Initial-Struct vom Backend, erlaubt
 * Inline-Edit, ↑↓ verschieben, +/- Positionen und Titel, finaler PDF-Export.
 */
export default function LVEditorModal({ open, onClose, analysisId, elementIds = [] }) {
  const modal = useModal();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState({ filename_label: '', floor_label: '', project_name: '' });
  const [editing, setEditing] = useState(null); // { titleId, posId, field } or { titleId, field: 'name' }

  // Initial laden
  useEffect(() => {
    if (!open || !analysisId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = elementIds.length ? `?element_ids=${elementIds.join(',')}` : '';
        const r = await axios.get(`${API}/api/analyses/${analysisId}/lv-draft${params}`);
        if (!cancelled) {
          setDraft(r.data.struct);
          setMeta({
            filename_label: r.data.filename_label,
            floor_label: r.data.floor_label,
            project_name: r.data.project_name,
          });
        }
      } catch (e) {
        console.error('LV-Draft load failed', e);
        modal.alert('LV-Draft konnte nicht geladen werden: ' + (e?.response?.data?.detail || e.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, analysisId, elementIds.join(','), modal]);

  // Helpers für Mutationen
  const updateTitle = (titleId, patch) => {
    setDraft(d => ({ ...d, titles: d.titles.map(t => t.id === titleId ? { ...t, ...patch } : t) }));
  };
  const updatePos = (titleId, posId, patch) => {
    setDraft(d => ({
      ...d,
      titles: d.titles.map(t => t.id === titleId ? {
        ...t,
        positions: t.positions.map(p => p.id === posId ? { ...p, ...patch } : p),
      } : t),
    }));
  };
  const movePos = (titleId, posId, dir) => {
    setDraft(d => ({
      ...d,
      titles: d.titles.map(t => {
        if (t.id !== titleId) return t;
        const idx = t.positions.findIndex(p => p.id === posId);
        if (idx < 0) return t;
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= t.positions.length) return t;
        const ps = [...t.positions];
        [ps[idx], ps[newIdx]] = [ps[newIdx], ps[idx]];
        return { ...t, positions: ps };
      }),
    }));
  };
  const moveTitle = (titleId, dir) => {
    setDraft(d => {
      const idx = d.titles.findIndex(t => t.id === titleId);
      if (idx < 0) return d;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= d.titles.length) return d;
      const ts = [...d.titles];
      [ts[idx], ts[newIdx]] = [ts[newIdx], ts[idx]];
      return { ...d, titles: ts };
    });
  };
  const deletePos = async (titleId, posId) => {
    if (!window.confirm('Position endgültig löschen?\n\nTipp: Über das Augen-Symbol kannst du sie auch nur ausblenden.')) return;
    setDraft(d => ({
      ...d,
      titles: d.titles.map(t => t.id === titleId ? {
        ...t, positions: t.positions.filter(p => p.id !== posId),
      } : t),
    }));
  };
  const deleteTitle = async (titleId) => {
    if (!window.confirm('Gesamten Titel mit allen Positionen endgültig löschen?\n\nTipp: Über das Augen-Symbol kannst du ihn auch nur ausblenden.')) return;
    setDraft(d => ({ ...d, titles: d.titles.filter(t => t.id !== titleId) }));
  };
  const togglePosHidden = (titleId, posId) => {
    setDraft(d => ({
      ...d,
      titles: d.titles.map(t => t.id === titleId ? {
        ...t,
        positions: t.positions.map(p => p.id === posId ? { ...p, hidden: !p.hidden } : p),
      } : t),
    }));
  };
  const toggleTitleHidden = (titleId) => {
    setDraft(d => ({
      ...d,
      titles: d.titles.map(t => t.id === titleId ? { ...t, hidden: !t.hidden } : t),
    }));
  };
  const addPos = (titleId) => {
    const newPos = {
      id: 'new-' + Math.random().toString(36).slice(2),
      oz: '',
      kurz: 'Neue Position',
      lang: '',
      qty: '',
      unit: 'm²',
    };
    setDraft(d => ({
      ...d,
      titles: d.titles.map(t => t.id === titleId ? { ...t, positions: [...t.positions, newPos] } : t),
    }));
    setEditing({ titleId, posId: newPos.id, field: 'kurz' });
  };
  const duplicatePos = (titleId, posId) => {
    setDraft(d => ({
      ...d,
      titles: d.titles.map(t => {
        if (t.id !== titleId) return t;
        const idx = t.positions.findIndex(p => p.id === posId);
        if (idx < 0) return t;
        const orig = t.positions[idx];
        const copy = {
          ...orig,
          id: 'dup-' + Math.random().toString(36).slice(2),
          oz: '',
          kurz: orig.kurz + ' (Kopie)',
        };
        const ps = [...t.positions];
        ps.splice(idx + 1, 0, copy);
        return { ...t, positions: ps };
      }),
    }));
  };
  const addTitle = () => {
    const next = (draft?.titles?.length || 0) + 1;
    const newTitle = {
      id: 'newT-' + Math.random().toString(36).slice(2),
      oz: String(next).padStart(2, '0'),
      name: 'NEUER TITEL',
      positions: [],
    };
    setDraft(d => ({ ...d, titles: [...(d?.titles || []), newTitle] }));
    setEditing({ titleId: newTitle.id, field: 'name' });
  };

  // Renumbering der OZ vor Export (optional, hilft Übersicht)
  // Hidden items werden NICHT mit-exportiert (im PDF nicht enthalten)
  const renumberAndExport = async () => {
    if (!draft) return;
    const visibleTitles = draft.titles.filter(t => !t.hidden);
    const renumbered = {
      ...draft,
      titles: visibleTitles.map((t, ti) => {
        const tOz = String(ti + 1).padStart(2, '0');
        const visiblePos = (t.positions || []).filter(p => !p.hidden);
        return {
          ...t,
          oz: tOz,
          positions: visiblePos.map((p, pi) => ({
            ...p,
            oz: `${tOz}.${String(pi + 1).padStart(2, '0')}`,
          })),
        };
      }),
    };
    setExporting(true);
    try {
      const resp = await axios.post(
        `${API}/api/analyses/${analysisId}/lv-draft/render-pdf`,
        {
          struct: renumbered,
          filename_label: meta.filename_label,
          floor_label: meta.floor_label,
        },
        { responseType: 'blob' }
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Langtext-LV_${meta.filename_label || 'editiert'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onClose?.();
    } catch (e) {
      console.error(e);
      modal.alert('PDF-Export fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4" data-testid="lv-editor-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-6xl h-full max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <div>
            <h2 className="text-zinc-100 font-semibold text-base">LV-Editor</h2>
            <div className="text-zinc-500 text-xs">
              {meta.project_name} · {meta.floor_label} · {meta.filename_label}
              {elementIds.length > 0 && <span className="ml-2 text-amber-400">— Auswahl ({elementIds.length} Elemente)</span>}
            </div>
          </div>
          <button onClick={onClose} data-testid="lv-editor-close"
            className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-20 text-zinc-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade LV-Draft …
            </div>
          )}
          {!loading && draft && draft.titles.map((title, tIdx) => (
            <TitleBlock key={title.id} title={title} tIdx={tIdx}
              isFirst={tIdx === 0} isLast={tIdx === draft.titles.length - 1}
              onTitleChange={(p) => updateTitle(title.id, p)}
              onTitleMove={(dir) => moveTitle(title.id, dir)}
              onTitleDelete={() => deleteTitle(title.id)}
              onTitleToggleHidden={() => toggleTitleHidden(title.id)}
              onPosChange={(posId, p) => updatePos(title.id, posId, p)}
              onPosMove={(posId, dir) => movePos(title.id, posId, dir)}
              onPosDelete={(posId) => deletePos(title.id, posId)}
              onPosDuplicate={(posId) => duplicatePos(title.id, posId)}
              onPosToggleHidden={(posId) => togglePosHidden(title.id, posId)}
              onPosAdd={() => addPos(title.id)}
              editing={editing} setEditing={setEditing} />
          ))}
          {!loading && draft && (
            <button onClick={addTitle} data-testid="lv-add-title"
              className="w-full px-3 py-2 rounded border border-dashed border-zinc-700 text-zinc-500 hover:bg-zinc-900 hover:border-zinc-600 hover:text-zinc-300 text-sm flex items-center justify-center gap-1.5">
              <Plus className="w-4 h-4" /> Neuen Titel hinzufügen
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800 bg-zinc-900/40">
          <div className="text-zinc-500 text-xs">
            {draft && (() => {
              const visibleTitles = draft.titles.filter(t => !t.hidden);
              const visiblePos = draft.titles.reduce(
                (s, t) => s + (t.hidden ? 0 : t.positions.filter(p => !p.hidden).length), 0);
              const hiddenTitles = draft.titles.length - visibleTitles.length;
              const totalPos = draft.titles.reduce((s, t) => s + t.positions.length, 0);
              const hiddenPos = totalPos - draft.titles.reduce(
                (s, t) => s + t.positions.filter(p => !p.hidden).length, 0);
              return (
                <>
                  <span className="text-emerald-400">{visibleTitles.length} Titel · {visiblePos} Positionen</span>
                  <span className="text-zinc-600"> im PDF</span>
                  {(hiddenTitles + hiddenPos) > 0 && (
                    <span className="ml-3 text-amber-500">⊘ {hiddenTitles + hiddenPos} ausgeblendet</span>
                  )}
                  {draft.skipped_openings_no_height > 0 && (
                    <span className="ml-3 text-amber-500">⚠ {draft.skipped_openings_no_height} Öffnung(en) ohne Höhe</span>
                  )}
                </>
              );
            })()}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} data-testid="lv-cancel"
              className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">
              Abbrechen
            </button>
            <button onClick={renumberAndExport} disabled={!draft || exporting}
              data-testid="lv-export-pdf"
              className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white text-xs font-semibold flex items-center gap-1.5">
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
              {exporting ? 'Exportiere …' : 'Als PDF exportieren'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
function TitleBlock({ title, tIdx, isFirst, isLast,
                     onTitleChange, onTitleMove, onTitleDelete, onTitleToggleHidden,
                     onPosChange, onPosMove, onPosDelete, onPosDuplicate, onPosToggleHidden, onPosAdd,
                     editing, setEditing }) {
  const titleHidden = !!title.hidden;
  return (
    <div className={`border rounded ${titleHidden ? 'border-zinc-800 bg-zinc-950 opacity-50' : 'border-zinc-800 bg-zinc-900/40'}`}>
      <div className={`flex items-center gap-2 px-3 py-2 border-b border-zinc-800 ${titleHidden ? 'bg-zinc-900/30' : 'bg-blue-900/20'}`}>
        <span className={`font-mono text-sm ${titleHidden ? 'text-zinc-600 line-through' : 'text-blue-400'}`}>{title.oz}</span>
        {editing?.titleId === title.id && editing.field === 'name' && !editing.posId ? (
          <input autoFocus value={title.name}
            onChange={e => onTitleChange({ name: e.target.value })}
            onBlur={() => setEditing(null)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(null); }}
            data-testid={`lv-title-edit-${tIdx}`}
            className="flex-1 bg-zinc-800 border border-blue-500 rounded px-2 py-0.5 text-zinc-100 text-sm font-semibold" />
        ) : (
          <span className={`flex-1 font-semibold text-sm cursor-pointer ${titleHidden ? 'text-zinc-500 line-through hover:text-zinc-400' : 'text-zinc-100 hover:text-blue-400'}`}
            data-testid={`lv-title-name-${tIdx}`}
            onClick={() => setEditing({ titleId: title.id, field: 'name' })}>
            {title.name}
            {titleHidden && <span className="ml-2 text-[10px] uppercase text-zinc-600">— ausgeblendet</span>}
            <Edit2 className="w-3 h-3 inline ml-1 opacity-40" />
          </span>
        )}
        <div className="flex gap-0.5">
          <IconBtn icon={titleHidden ? EyeOff : Eye} onClick={onTitleToggleHidden}
            title={titleHidden ? 'Titel wieder einblenden (im LV anzeigen)' : 'Titel ausblenden (nicht im LV)'}
            testId={`lv-title-hide-${tIdx}`} variant={titleHidden ? 'muted' : undefined} />
          <IconBtn icon={ArrowUp} disabled={isFirst} onClick={() => onTitleMove(-1)} title="Titel hoch" testId={`lv-title-up-${tIdx}`} />
          <IconBtn icon={ArrowDown} disabled={isLast} onClick={() => onTitleMove(1)} title="Titel runter" testId={`lv-title-down-${tIdx}`} />
          <IconBtn icon={Trash2} variant="danger" onClick={onTitleDelete} title="Titel endgültig löschen" testId={`lv-title-del-${tIdx}`} />
        </div>
      </div>
      <div className="p-2 space-y-1.5">
        {title.positions.map((p, pi) => (
          <PositionRow key={p.id} pos={p} pi={pi}
            isFirst={pi === 0} isLast={pi === title.positions.length - 1}
            onChange={(patch) => onPosChange(p.id, patch)}
            onMove={(dir) => onPosMove(p.id, dir)}
            onDelete={() => onPosDelete(p.id)}
            onDuplicate={() => onPosDuplicate(p.id)}
            onToggleHidden={() => onPosToggleHidden(p.id)}
            editing={editing} setEditing={setEditing} titleId={title.id} />
        ))}
        {title.positions.length === 0 && (
          <div className="text-zinc-600 text-xs italic px-2 py-1">Keine Positionen — leerer Titel.</div>
        )}
        <button onClick={onPosAdd} data-testid={`lv-pos-add-${tIdx}`}
          className="w-full px-2 py-1 mt-1 rounded border border-dashed border-zinc-700 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 text-xs flex items-center justify-center gap-1">
          <Plus className="w-3 h-3" /> Position hinzufügen
        </button>
      </div>
    </div>
  );
}

function PositionRow({ pos, pi, isFirst, isLast, onChange, onMove, onDelete, onDuplicate, onToggleHidden, editing, setEditing, titleId }) {
  const isEditing = (field) => editing?.posId === pos.id && editing?.field === field;
  const hidden = !!pos.hidden;
  return (
    <div className={`border rounded p-2 space-y-1 ${hidden ? 'border-zinc-800 bg-zinc-950/60 opacity-50' : 'border-zinc-800 bg-zinc-900/30'}`}>
      <div className="flex items-start gap-2">
        <span className={`font-mono text-xs pt-0.5 w-10 shrink-0 ${hidden ? 'text-zinc-600 line-through' : 'text-zinc-500'}`}>{pos.oz || `?.${pi + 1}`}</span>
        <div className="flex-1 min-w-0">
          {isEditing('kurz') ? (
            <input autoFocus value={pos.kurz}
              onChange={e => onChange({ kurz: e.target.value })}
              onBlur={() => setEditing(null)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(null); }}
              data-testid={`lv-pos-kurz-edit-${pos.id}`}
              className="w-full bg-zinc-800 border border-blue-500 rounded px-2 py-0.5 text-zinc-100 text-sm" />
          ) : (
            <div className={`text-sm cursor-pointer ${hidden ? 'text-zinc-500 line-through hover:text-zinc-400' : 'text-zinc-200 hover:text-blue-400'}`}
              data-testid={`lv-pos-kurz-${pos.id}`}
              onClick={() => setEditing({ titleId, posId: pos.id, field: 'kurz' })}>
              {pos.kurz}
              {hidden && <span className="ml-2 text-[10px] uppercase text-zinc-600 not-italic">— ausgeblendet</span>}
              <Edit2 className="w-3 h-3 inline ml-1 opacity-30" />
            </div>
          )}
          {isEditing('lang') ? (
            <textarea autoFocus value={pos.lang || ''}
              onChange={e => onChange({ lang: e.target.value })}
              onBlur={() => setEditing(null)}
              data-testid={`lv-pos-lang-edit-${pos.id}`}
              rows={4}
              className="mt-1 w-full bg-zinc-800 border border-blue-500 rounded px-2 py-1 text-zinc-300 text-xs font-mono" />
          ) : (
            <div className="mt-0.5 text-zinc-500 text-[11px] whitespace-pre-line cursor-pointer hover:text-zinc-300 border border-transparent hover:border-zinc-700 rounded px-1"
              data-testid={`lv-pos-lang-${pos.id}`}
              onClick={() => setEditing({ titleId, posId: pos.id, field: 'lang' })}>
              {pos.lang || <span className="italic text-zinc-600">+ Langtext hinzufügen</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isEditing('qty') ? (
            <input autoFocus type="number" step="0.01" value={pos.qty ?? ''}
              onChange={e => onChange({ qty: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              onBlur={() => setEditing(null)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(null); }}
              data-testid={`lv-pos-qty-edit-${pos.id}`}
              className="w-20 bg-zinc-800 border border-blue-500 rounded px-1.5 py-0.5 text-zinc-100 text-xs font-mono text-right" />
          ) : (
            <span className="font-mono text-emerald-400 text-sm cursor-pointer hover:bg-zinc-800 px-1.5 py-0.5 rounded"
              data-testid={`lv-pos-qty-${pos.id}`}
              onClick={() => setEditing({ titleId, posId: pos.id, field: 'qty' })}>
              {pos.qty != null && pos.qty !== '' ? pos.qty : '—'}
            </span>
          )}
          {isEditing('unit') ? (
            <input autoFocus value={pos.unit || ''}
              onChange={e => onChange({ unit: e.target.value })}
              onBlur={() => setEditing(null)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(null); }}
              data-testid={`lv-pos-unit-edit-${pos.id}`}
              className="w-12 bg-zinc-800 border border-blue-500 rounded px-1.5 py-0.5 text-zinc-100 text-xs" />
          ) : (
            <span className="font-mono text-zinc-400 text-xs cursor-pointer hover:bg-zinc-800 px-1 py-0.5 rounded w-10 text-center"
              data-testid={`lv-pos-unit-${pos.id}`}
              onClick={() => setEditing({ titleId, posId: pos.id, field: 'unit' })}>
              {pos.unit || '—'}
            </span>
          )}
        </div>
        <div className="flex gap-0.5 shrink-0">
          <IconBtn icon={hidden ? EyeOff : Eye} small onClick={onToggleHidden}
            title={hidden ? 'Position wieder einblenden' : 'Position ausblenden (nicht im LV)'}
            testId={`lv-pos-hide-${pos.id}`} variant={hidden ? 'muted' : undefined} />
          <IconBtn icon={ArrowUp} small disabled={isFirst} onClick={() => onMove(-1)} title="Position hoch" testId={`lv-pos-up-${pos.id}`} />
          <IconBtn icon={ArrowDown} small disabled={isLast} onClick={() => onMove(1)} title="Position runter" testId={`lv-pos-down-${pos.id}`} />
          <IconBtn icon={Copy} small onClick={onDuplicate} title="Position duplizieren" testId={`lv-pos-dup-${pos.id}`} />
          <IconBtn icon={Trash2} small variant="danger" onClick={onDelete} title="Position endgültig löschen" testId={`lv-pos-del-${pos.id}`} />
        </div>
      </div>
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, disabled, title, variant, small, testId }) {
  const cls = disabled
    ? 'p-1 text-zinc-700 cursor-not-allowed'
    : variant === 'danger'
      ? 'p-1 text-zinc-500 hover:text-red-400 hover:bg-red-900/30 rounded'
      : variant === 'muted'
        ? 'p-1 text-amber-500/70 hover:text-amber-400 hover:bg-zinc-800 rounded'
        : 'p-1 text-zinc-500 hover:text-blue-400 hover:bg-zinc-800 rounded';
  return (
    <button onClick={onClick} disabled={disabled} title={title} data-testid={testId} className={cls}>
      <Icon className={small ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
    </button>
  );
}
